import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon, CheckCircle2Icon, Loader2Icon, XIcon } from 'lucide-react';
interface TalkToUsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  organization: string;
  intent: string[];
}
const INITIAL_DATA: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  organization: '',
  intent: []
};
const intentOptions = ['Connect coaching over time', 'Support curriculum implementation', 'Create a clearer school or district view', 'Explore a partnership'];
export const TalkToUsOverlay: React.FC<TalkToUsOverlayProps> = ({
  isOpen,
  onClose
}) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.setTimeout(() => firstInputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = 'unset';
    }
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = event.target;
    setFormData(previous => ({
      ...previous,
      [name]: value
    }));
  };
  const handleIntentToggle = (option: string) => {
    setFormData(previous => ({
      ...previous,
      intent: previous.intent.includes(option) ? previous.intent.filter(item => item !== option) : [...previous.intent, option]
    }));
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    const payload: Record<string, unknown> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      role: formData.role,
      organization: formData.organization,
      source: 'marketing-web'
    };
    if (formData.intent.length > 0) payload.goals = formData.intent;
    try {
      const response = await fetch('https://app.nisa.coach/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Lead request failed');
      setIsSuccess(true);
      setFormData(INITIAL_DATA);
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    if (isSuccess) window.setTimeout(() => setIsSuccess(false), 300);
    onClose();
  };
  if (!isOpen) return null;
  const inputClassName = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-600';
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-white animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="talk-to-us-title" data-id="element-339" data-mp="mx2xgy5tpqxd">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] overflow-hidden" data-id="element-340" data-mp="3qci7w0i462j1">
        <div className="absolute -right-40 -top-32 h-[640px] w-[760px] rounded-[60%_40%_55%_45%] bg-gradient-to-br from-cyan-200/25 via-teal-200/20 to-cyan-100/10 blur-3xl" data-id="element-341" data-mp="8w0b0kax2elx" />
        <div className="absolute -left-48 -top-20 h-[560px] w-[680px] rounded-[40%_60%_45%_55%] bg-gradient-to-tr from-cyan-200/20 via-cyan-100/15 to-teal-200/10 blur-3xl" data-id="element-342" data-mp="4374przsuy7q" />
      </div>

      <div className="sticky top-0 z-10 flex h-16 items-center justify-between bg-white/90 px-4 backdrop-blur-md sm:px-6 lg:px-8" data-id="element-343" data-mp="37lh6l5tituwb">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-6 w-auto sm:h-8" data-id="element-344" data-mp="2ew725y552elv" />
        <button type="button" onClick={handleClose} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-900" aria-label="Close" data-id="element-345" data-mp="2jtqs3vb78ftn">
          <XIcon className="h-6 w-6" data-id="element-346" data-mp="2tlwhqydzal5o" />
        </button>
      </div>

      <div className="relative mx-auto flex min-h-[calc(100vh-64px)] w-full max-w-3xl items-center px-4 py-10 sm:px-6 md:py-14" data-id="element-347" data-mp="2tng1x02r9gcq">
        <div className="w-full animate-slide-up" data-id="element-348" data-mp="32ljl3pq9q11d">
          {isSuccess ? <div className="mx-auto max-w-xl py-12 text-center" data-id="element-349" data-mp="32jyrc2bs2m4k">
              <div className="mb-7 inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700" data-id="element-350" data-mp="37kcogpbucbnt">
                <CheckCircle2Icon className="h-8 w-8" data-id="element-351" data-mp="18a4uz7tchzl3" />
              </div>
              <h2 className="font-serif text-3xl text-teal-950 md:text-4xl" data-id="element-352" data-mp="1d73njlxd1yh6">
                Thanks, we have your request
              </h2>
              <p className="mx-auto mt-5 max-w-lg text-lg leading-relaxed text-gray-600" data-id="element-353" data-mp="10wvt6fsx8dij">
                We&apos;ll follow up by email to schedule a conversation about
                your coaching program.
              </p>
              <button type="button" onClick={handleClose} className="mt-8 font-medium text-primary-700 underline decoration-primary-300 underline-offset-4 hover:text-primary-800" data-id="element-354" data-mp="qzheolqk5ysv">
                Back to Nisa
              </button>
            </div> : <>
              <div className="text-center" data-id="element-355" data-mp="1jlahgvbutdfn">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-356" data-mp="1emzcaxwi7r3r">
                  For schools, districts, and partners
                </p>
                <h1 id="talk-to-us-title" className="font-serif text-3xl font-medium text-teal-950 md:text-4xl" data-id="element-357" data-mp="39dcmit0q73z">
                  Request a Nisa demo
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-gray-600" data-id="element-358" data-mp="3vf0p7nx3kjv2">
                  Tell us about your organization and coaching program.
                  We&apos;ll follow up to schedule a walkthrough shaped around
                  your work.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-2xl space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8" data-id="element-359" data-mp="3rugqhfi8voly">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-360" data-mp="3e1r6fgma0trh">
                  <div data-id="element-361" data-mp="3iv3r5fnu4gey">
                    <label htmlFor="firstName" className="mb-1.5 block text-sm font-medium text-gray-700" data-id="element-362" data-mp="3ivlj403gs8h2">
                      First name
                    </label>
                    <input ref={firstInputRef} id="firstName" name="firstName" type="text" required value={formData.firstName} onChange={handleChange} className={inputClassName} data-id="element-363" data-mp="393fw74s2kh8q" />
                  </div>
                  <div data-id="element-364" data-mp="2z501krtu2puh">
                    <label htmlFor="lastName" className="mb-1.5 block text-sm font-medium text-gray-700" data-id="element-365" data-mp="2z5jn0iiozwd9">
                      Last name
                    </label>
                    <input id="lastName" name="lastName" type="text" required value={formData.lastName} onChange={handleChange} className={inputClassName} data-id="element-366" data-mp="2bjfy7kk0feny" />
                  </div>
                </div>

                <div data-id="element-367" data-mp="2kj2yg3106eff">
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700" data-id="element-368" data-mp="2khsyt9ei4y6o">
                    Work email
                  </label>
                  <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className={inputClassName} data-id="element-369" data-mp="2uhsavga4r6oa" />
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-370" data-mp="38b1gr6yvda22">
                  <div data-id="element-371" data-mp="347vc23renqxp">
                    <label htmlFor="organization" className="mb-1.5 block text-sm font-medium text-gray-700" data-id="element-372" data-mp="349g33jeigrqt">
                      Organization
                    </label>
                    <input id="organization" name="organization" type="text" required value={formData.organization} onChange={handleChange} className={inputClassName} data-id="element-373" data-mp="3d9l152xtedsb" />
                  </div>
                  <div data-id="element-374" data-mp="sfd9lnd0e3u8">
                    <label htmlFor="role" className="mb-1.5 block text-sm font-medium text-gray-700" data-id="element-375" data-mp="sirg97spgz7h">
                      Role
                    </label>
                    <select id="role" name="role" required value={formData.role} onChange={handleChange} className={inputClassName} data-id="element-376" data-mp="12dt480b8vv8h">
                      <option value="" disabled data-id="element-377" data-mp="4sx1ulsqn2l4">
                        Select your role
                      </option>
                      <option value="District or network leader" data-id="element-378" data-mp="9nu85kymuxub">
                        District or network leader
                      </option>
                      <option value="School leader" data-id="element-379" data-mp="jh1mhvbcpo6j">School leader</option>
                      <option value="Instructional coaching leader" data-id="element-380" data-mp="3i6ibtz3d488h">
                        Instructional coaching leader
                      </option>
                      <option value="Instructional coach" data-id="element-381" data-mp="3n38soomzr7uy">
                        Instructional coach
                      </option>
                      <option value="Curriculum or implementation partner" data-id="element-382" data-mp="3amqw2gehcexs">
                        Curriculum or implementation partner
                      </option>
                      <option value="Other" data-id="element-383" data-mp="35phaxcy4ipfr">Other</option>
                    </select>
                  </div>
                </div>

                <fieldset data-id="element-384" data-mp="2wmg57dd670cm">
                  <legend className="text-sm font-medium text-gray-700" data-id="element-385" data-mp="2wnrjw885lsii">
                    What would be most useful to explore?
                    <span className="ml-2 font-normal text-gray-500" data-id="element-386" data-mp="2ruopjxvjyf19">
                      Optional
                    </span>
                  </legend>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-387" data-mp="2hybt4s8mhn2t">
                    {intentOptions.map(option => <label key={option} className={`inline-flex cursor-pointer items-center rounded-lg border px-3.5 py-2 text-sm transition-all ${formData.intent.includes(option) ? 'border-primary-500 bg-primary-50 text-primary-700 ring-2 ring-primary-500/20' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`} data-id="element-388" data-mp="2cz8q1dc7dqxh">
                        <input type="checkbox" value={option} checked={formData.intent.includes(option)} onChange={() => handleIntentToggle(option)} className="sr-only" data-id="element-389" data-mp="2pnd4ayl1s8yf" />
                        {option}
                      </label>)}
                  </div>
                </fieldset>

                <div className="border-t border-gray-100 pt-6" data-id="element-390" data-mp="2bpg4ltbarmfj">
                  {submitError && <p className="mb-4 text-sm font-medium text-red-600" role="alert" data-id="element-391" data-mp="26ptqlhnna25o">
                      {submitError}
                    </p>}
                  <button type="submit" disabled={isSubmitting} className="inline-flex w-full items-center justify-center rounded-xl bg-primary-700 px-7 py-3 font-medium text-white shadow-sm transition-colors hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-600 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-70 sm:w-auto" data-id="element-392" data-mp="2kqml42dy4x8s">
                    {isSubmitting ? <>
                        <Loader2Icon className="mr-2 h-5 w-5 animate-spin" data-id="element-393" data-mp="3fgna3qaudoz" />
                        Sending
                      </> : <>
                        Request a Nisa demo
                        <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-394" data-mp="3pp9qzl2b6ie0" />
                      </>}
                  </button>
                  <p className="mt-3 text-xs leading-relaxed text-gray-500" data-id="element-395" data-mp="3unbag73ncjq0">
                    We&apos;ll follow up by email to schedule a conversation.
                    Submitting this form does not create an account.
                  </p>
                </div>
              </form>
            </>}
        </div>
      </div>
    </div>;
};